<script lang="ts">
  import { onMount } from 'svelte';
  import { ApiClient } from './lib/api';
  import { AuthState } from './lib/auth.svelte';
  import { Router } from './lib/router.svelte';
  import type { RuntimeConfig } from './lib/types';
  import Loading from './lib/Loading.svelte';
  import CallbackPage from './pages/CallbackPage.svelte';
  import CustomerPage from './pages/CustomerPage.svelte';
  import CustomersPage from './pages/CustomersPage.svelte';
  import GuardDutyFindingPage from './pages/GuardDutyFindingPage.svelte';
  import GuardDutyPage from './pages/GuardDutyPage.svelte';
  import HistoryPage from './pages/HistoryPage.svelte';
  import IncidentPage from './pages/IncidentPage.svelte';
  import LoginPage from './pages/LoginPage.svelte';
  import OverviewPage from './pages/OverviewPage.svelte';
  import SuppressionsPage from './pages/SuppressionsPage.svelte';

  const auth = new AuthState();
  const router = new Router();
  const api = new ApiClient(auth);
  let initialised = $state(false);
  let startupError = $state('');

  onMount(async () => {
    try {
      const response = await fetch('/app-config.json', { cache: 'no-store' });
      if (!response.ok) throw new Error('Runtime configuration is unavailable');
      const config = (await response.json()) as RuntimeConfig;
      await auth.initialise(config);
    } catch (error) {
      startupError = error instanceof Error ? error.message : 'The application could not start.';
    } finally {
      initialised = true;
    }
  });

  function navigate(event: MouseEvent, path: string) {
    event.preventDefault();
    router.navigate(path);
  }
</script>

{#if !initialised}
  <main class="centred"><Loading /></main>
{:else if startupError}
  <main class="centred error-panel"><h1>Alert Receiver unavailable</h1><p>{startupError}</p></main>
{:else if router.path === '/auth/callback'}
  <CallbackPage {auth} {router} />
{:else if auth.status !== 'authenticated'}
  <LoginPage {auth} />
{:else}
  <div class="app-shell">
    <header>
      <a class="brand" href="/" onclick={(event) => navigate(event, '/')} aria-label="Alert Receiver home">
        <img class="brand-mark" src="/corrected-cloud-logo.svg" alt="" />
        <div><strong>Alert Receiver</strong><small>Corrected Cloud operations</small></div>
      </a>
      <nav aria-label="Primary navigation">
        <a class:active={router.path === '/'} href="/" onclick={(event) => navigate(event, '/')}>Current</a>
        <a class:active={router.path === '/history'} href="/history" onclick={(event) => navigate(event, '/history')}>History</a>
        <a class:active={router.path === '/guardduty' || router.path.startsWith('/guardduty/findings/')} href="/guardduty" onclick={(event) => navigate(event, '/guardduty')}>GuardDuty</a>
        <a class:active={router.path === '/guardduty/suppressions'} href="/guardduty/suppressions" onclick={(event) => navigate(event, '/guardduty/suppressions')}>Suppressions</a>
        <a class:active={router.path === '/customers' || router.path.startsWith('/customers/')} href="/customers" onclick={(event) => navigate(event, '/customers')}>Customers</a>
      </nav>
      <button class="quiet" onclick={() => auth.signOut()}>Sign out</button>
    </header>
    <main id="main-content">
      {#if router.path === '/'}
        <OverviewPage {api} />
      {:else if router.path === '/history'}
        <HistoryPage {api} />
      {:else if router.path === '/guardduty'}
        <GuardDutyPage {api} />
      {:else if router.path === '/guardduty/suppressions'}
        <SuppressionsPage {api} canManage={auth.hasScope('alertreceiver/manage')} />
      {:else if router.path.startsWith('/guardduty/findings/')}
        <GuardDutyFindingPage {api} {router} canManage={auth.hasScope('alertreceiver/manage')} findingId={decodeURIComponent(router.path.slice('/guardduty/findings/'.length))} />
      {:else if router.path === '/customers'}
        <CustomersPage {api} {router} />
      {:else if router.path.startsWith('/customers/')}
        <CustomerPage {api} {router} customerId={decodeURIComponent(router.path.slice('/customers/'.length))} />
      {:else if router.path.startsWith('/incidents/')}
        <IncidentPage {api} {router} incidentId={decodeURIComponent(router.path.slice('/incidents/'.length))} />
      {:else}
        <section class="error-panel"><p class="eyebrow">404</p><h1>Page not found</h1><a href="/" onclick={(event) => navigate(event, '/')}>Return to current incidents</a></section>
      {/if}
    </main>
  </div>
{/if}
